/**
 * @fileoverview Vendor Invoice Upload Dialog Component
 * @module VendorInvoiceDialog
 * 
 * Business Context:
 * - Modal dialog for uploading vendor invoices with cost and scope information
 * - Allows users to attach repair estimates/invoices to strengthen their appeal
 * - Data flows into the final evidence packet for the assessor meeting
 */

import React, { useState, useCallback } from 'react';
import { X, Upload, FileText, Loader2, DollarSign, AlertCircle } from 'lucide-react';
import { apiClient } from '../../api/client';

interface VendorInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  findingId: string;
  onUploadComplete: (invoice: any) => void;
  categoryName?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const VendorInvoiceDialog: React.FC<VendorInvoiceDialogProps> = ({
  isOpen,
  onClose,
  findingId,
  onUploadComplete,
  categoryName = 'this category'
}) => {
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setAmount('');
    setDescription('');
    setSelectedFile(null);
    setError(null);
    setUploadProgress(0);
  };

  const handleClose = () => {
    if (!uploading) {
      resetForm();
      onClose();
    }
  };

  const validateFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') {
      return 'Only PDF files are allowed for invoices.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 10MB.';
    }
    return null;
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
    e.target.value = ''; // Reset input
  };

  const parseAmount = (value: string): number => {
    // Remove any non-numeric characters except decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned);
    if (isNaN(dollars)) return 0;
    // Convert to cents
    return Math.round(dollars * 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!selectedFile) {
      setError('Please select a PDF file to upload.');
      return;
    }

    const amountInCents = parseAmount(amount);
    if (amountInCents <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    if (!description.trim()) {
      setError('Please describe the scope of work.');
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      // Step 1: Get presigned URL
      const urlResponse = await apiClient.evidence.getInvoiceUploadUrl({
        findingId,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size
      });

      if (!urlResponse.data.success) {
        throw new Error(urlResponse.data.error?.message || 'Failed to get upload URL');
      }

      const { uploadUrl, s3Key } = urlResponse.data.data;
      setUploadProgress(30);

      // Step 2: Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      setUploadProgress(70);

      // Step 3: Confirm upload with metadata
      const confirmResponse = await apiClient.evidence.confirmInvoice({
        findingId,
        s3Key,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        amount: amountInCents,
        description: description.trim()
      });

      if (!confirmResponse.data.success) {
        throw new Error(confirmResponse.data.error?.message || 'Failed to confirm upload');
      }

      setUploadProgress(100);

      // Success - notify parent and close
      onUploadComplete(confirmResponse.data.data);
      
      // Short delay to show completion
      setTimeout(() => {
        handleClose();
      }, 500);

    } catch (err: any) {
      console.error('Invoice upload error:', err);
      setError(err.message || 'Failed to upload invoice. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Add Vendor Invoice</h2>
                <p className="text-sm text-gray-500">Upload a repair estimate or invoice for {categoryName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={uploading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invoice Amount
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                disabled={uploading}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Enter the total amount from the invoice</p>
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scope of Work
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Briefly describe what work the invoice covers (e.g., 'Full roof replacement with 30-year architectural shingles')"
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              disabled={uploading}
            />
            <p className="mt-1 text-xs text-gray-500">{description.length}/1000 characters</p>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invoice PDF
            </label>
            <label
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`
                relative block w-full p-6 border-2 border-dashed rounded-xl cursor-pointer
                transition-all duration-200
                ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
                ${dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : selectedFile
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                }
              `}
            >
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileSelect}
                className="sr-only"
                disabled={uploading}
              />
              <div className="flex flex-col items-center text-center">
                {selectedFile ? (
                  <>
                    <FileText className="w-10 h-10 mb-2 text-green-500" />
                    <p className="text-gray-900 font-medium">{selectedFile.name}</p>
                    <p className="text-gray-500 text-sm mt-1">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    {!uploading && (
                      <p className="text-blue-600 text-sm mt-2">Click or drag to replace</p>
                    )}
                  </>
                ) : (
                  <>
                    <Upload className={`w-10 h-10 mb-2 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
                    <p className="text-gray-700 font-medium">
                      {dragActive ? 'Drop PDF here' : 'Drag & drop PDF or click to browse'}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      PDF only • Max 10MB
                    </p>
                  </>
                )}
              </div>
            </label>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Uploading...</span>
                <span className="text-gray-900 font-medium">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !selectedFile || !amount || !description.trim()}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload Invoice
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorInvoiceDialog;







